import React, { ReactElement, ReactNode } from "react";
import clsx from "clsx";

interface CustomHorizontalTabProps {
    selectedIndex: number;
    onTabClick: (index: number) => void;
    children: ReactNode[];
}

export function CustomHorizontalTab({ selectedIndex, onTabClick, children }: CustomHorizontalTabProps) {
    return (
        <div className="flex flex-row bg-white border-b-2 border-brandPrimary-200 px-4 gap-1">
            {React.Children.map(children, (child, index) => {
                if (!React.isValidElement(child)) return null;
                const isSelected = selectedIndex === index;
                const hasError = child.props['data-has-error'];
                const isDisabled = child.props['data-disabled'] || child.props.disabled;

                return React.cloneElement(child as ReactElement, {
                    onClick: () => {
                        if (!isDisabled) {
                            onTabClick(index);
                        }
                    },
                    style: { fontFamily: 'Manrope', ...(child.props.style || {}) },
                    className: clsx(
                        "relative px-5 py-4 flex items-center transition-all duration-200 text-sm font-medium select-none whitespace-nowrap",
                        child.props.className || "",
                        isDisabled ? "cursor-not-allowed text-neutral-300" : "cursor-pointer",
                        !isDisabled && isSelected
                            ? (hasError ? "text-red-600 font-semibold" : "text-brandPrimary-700 font-semibold")
                            : null,
                        !isDisabled && !isSelected
                            ? (hasError ? "text-red-500 hover:text-red-600 hover:bg-neutral-50 rounded-t-lg" : "text-neutral-400 hover:text-neutral-600 hover:bg-neutral-50 rounded-t-lg")
                            : null
                    ),
                    children: (
                        <>
                            {child.props.children}
                            <span
                                className={clsx(
                                    "absolute bottom-[-2px] left-0 right-0 h-[3px] rounded-t-full transition-all duration-300",
                                    (isSelected && !isDisabled)
                                        ? (hasError ? "bg-red-600" : "bg-brandPrimary-600") 
                                        : (!isDisabled && hasError ? "bg-red-400" : "bg-transparent")
                                )}
                            />
                        </>
                    ),
                });
            })}
        </div>
    );
}